/**
 * Copyright © 2026 Adnuntius AS.
 */
import {resourceOptionParams} from '../../common/controller/signal-admin-resource-api';
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import {firstValueFrom, forkJoin} from 'rxjs';

import template from './mcp-authorize-page.component.html';
import {ApiConfig} from '../../config/api-config';
import {
  AGENT_LEASE_HOURS,
  oauthErrorMessage,
  VALID_OAUTH_REDIRECT
} from './api-key-commons';
import {AdnHttpRequest} from '../../../components/util/adn-http.service';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {Session} from '../../../components/session/session';
import {WINDOW} from '../../angular-platform-providers';
import {NetworkApiService} from '../network/network-api.service';
import {AdnNetworkHelperService} from '../../../components/util/adn-network-helper.service';
import {AppEventBusService} from '../../common/events/app-event-bus.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {AppSharedModule} from '../../common/app-shared.module';
import {TeamApiService} from '../team/team-api.service';
import {MarketplaceProductApiService} from '../marketplace-product/marketplace-product-api.service';

const BOOKING_SUFFIX = '/booking';
const QUERY_SCOPES = ['ADVERTISING_QUERY', 'STATS_QUERY', 'PUBLISHING_QUERY'];
const BOOKING_SCOPES = ['ADVERTISING_AGENT_BOOKING', 'ADVERTISING_QUERY'];

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, ButtonsModule],
  selector: 'adn-mcp-authorize-page',
  template,
  styles: [`
    .mcp-authorize-wrap {
      display: flex;
      justify-content: center;
      padding: 12vh 16px 40px;
    }
    .mcp-authorize {
      width: 100%;
      max-width: 480px;
      padding: 24px;
      background: #fff;
      border: 1px solid var(--adn-gl-border-default, #ddd);
      border-radius: 4px;
    }
    .mcp-authorize h1 {
      font-size: 20px;
      font-weight: 500;
      line-height: 1.3;
      margin: 0 0 8px;
      text-align: center;
    }
    .mcp-perms {
      list-style: none;
      margin: 16px 0 20px;
      padding: 0;
      border-top: 1px solid #e5e5e5;
    }
    .mcp-perms li {
      padding: 14px 0;
      border-bottom: 1px solid #e5e5e5;
    }
    .mcp-authorize-support {
      display: block;
      margin: 0 0 20px;
      font-weight: normal;
      color: #555;
    }
    .mcp-authorize-support input {
      margin-right: 8px;
    }
    .mcp-legal {
      margin: 0 0 24px;
      font-size: 12px;
      line-height: 1.5;
      color: #777;
    }
    .mcp-legal a,
    .mcp-authorize .alert a {
      color: inherit;
      text-decoration: underline;
    }
    .mcp-authorize .alert h2 {
      font-size: 16px;
      font-weight: 600;
      line-height: 1.3;
      margin: 0 0 8px;
    }
    .mcp-authorize .alert p {
      margin: 0 0 8px;
    }
    .mcp-authorize .alert p:last-child {
      margin-bottom: 0;
    }
    .mcp-network {
      margin: 0 0 20px;
    }
    .mcp-network label {
      font-weight: normal;
      color: #555;
    }
    .mcp-booking {
      margin: 0 0 20px;
      color: #555;
    }
    .mcp-booking label {
      display: block;
      font-weight: normal;
    }
    .mcp-booking input {
      margin-right: 8px;
    }
    .mcp-actions {
      text-align: right;
    }
    .mcp-actions .btn + .btn {
      margin-left: 16px;
    }
  `]
})
export class McpAuthorizePageComponent implements OnInit, OnDestroy {
  private readonly send: (config: AdnHttpRequest) => Promise<any>;

  oauthCodeChallenge: string | null = null;
  oauthCodeChallengeMethod = 'S256';
  oauthRedirectUri: string | null = null;
  oauthState = '';
  oauthClientId: string | null = null;
  agentOAuth = false;
  agentApp: string | null = null;
  private readonly oauthErrorState = signal<any>(null);
  private readonly oauthErrorMessageState = signal<string | null>(null);
  private readonly authorizingState = signal(false);
  private readonly networkNameState = signal('Adnuntius');
  private readonly currentNetworkIdState = signal<string | null>(null);
  private readonly currentNetworkTagIdState = signal<string | null>(null);
  private readonly networkChangedElsewhereState = signal(false);
  private readonly networksState = signal<any[] | null>(null);
  private readonly networksLoadFailedState = signal(false);
  private readonly selectedNetworkIdState = signal<string | null>(null);
  private readonly switchingToState = signal<any>(null);
  private readonly switchFailedNameState = signal<string | null>(null);
  private readonly bookingTeamsState = signal<any[] | null>(null);
  private readonly bookingProductsState = signal<any[] | null>(null);
  private readonly bookingLoadFailedState = signal(false);

  get oauthError(): any { return this.oauthErrorState(); }
  set oauthError(value: any) { this.oauthErrorState.set(value); }
  get oauthErrorMessage(): string | null { return this.oauthErrorMessageState(); }
  set oauthErrorMessage(value: string | null) { this.oauthErrorMessageState.set(value); }
  get authorizing(): boolean { return this.authorizingState(); }
  set authorizing(value: boolean) { this.authorizingState.set(value); }
  get networkName(): string { return this.networkNameState(); }
  set networkName(value: string) { this.networkNameState.set(value); }
  get currentNetworkId(): string | null { return this.currentNetworkIdState(); }
  set currentNetworkId(value: string | null) { this.currentNetworkIdState.set(value); }
  get currentNetworkTagId(): string | null { return this.currentNetworkTagIdState(); }
  set currentNetworkTagId(value: string | null) { this.currentNetworkTagIdState.set(value); }
  get networkChangedElsewhere(): boolean { return this.networkChangedElsewhereState(); }
  set networkChangedElsewhere(value: boolean) { this.networkChangedElsewhereState.set(value); }
  get networks(): any[] | null { return this.networksState(); }
  set networks(value: any[] | null) { this.networksState.set(value); }
  get networksLoadFailed(): boolean { return this.networksLoadFailedState(); }
  set networksLoadFailed(value: boolean) { this.networksLoadFailedState.set(value); }
  get selectedNetworkId(): string | null { return this.selectedNetworkIdState(); }
  set selectedNetworkId(value: string | null) { this.selectedNetworkIdState.set(value); }
  get switchingTo(): any { return this.switchingToState(); }
  set switchingTo(value: any) { this.switchingToState.set(value); }
  get switchFailedName(): string | null { return this.switchFailedNameState(); }
  set switchFailedName(value: string | null) { this.switchFailedNameState.set(value); }
  get bookingTeams(): any[] | null { return this.bookingTeamsState(); }
  set bookingTeams(value: any[] | null) { this.bookingTeamsState.set(value); }
  get bookingProducts(): any[] | null { return this.bookingProductsState(); }
  set bookingProducts(value: any[] | null) { this.bookingProductsState.set(value); }
  get bookingLoadFailed(): boolean { return this.bookingLoadFailedState(); }
  set bookingLoadFailed(value: boolean) { this.bookingLoadFailedState.set(value); }
  oauthRedirectHost: string | null = null;
  oauthRedirectIsLoopback = false;
  supportEligible = false;
  supportRequested = false;
  oauthResource: string | null = null;
  clientDeclaredSupport = false;
  cancelled = false;
  hasMultipleNetworks = false;
  twoFactorAuthEnabled = false;
  // The network the page names. Other tabs can switch the shared session's network at any time, so the page reads it again before it acts.
  // The network a per-network connector (<mcp>/<networkId>) is for. The server mints its lease only in that network.
  resourceNetworkId: string | null = null;
  // A connector for the agent booking endpoint (<mcp>/<networkId>/booking) gets a key that books only for the buyer teams chosen here.
  bookingRequested = false;
  chosenTeamIds: string[] = [];
  chosenProductIds: string[] = [];

  constructor(
    @Inject(WINDOW) private readonly window: Window,
    apiClient: AdnApiClientService,
    private readonly uiRouter: UIRouter,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly localNetworkProfile: any,
    @Inject(NetworkApiService) private readonly Network: NetworkApiService,
    @Inject(AdnNetworkHelperService) private readonly adnNetworkHelper: any,
    private readonly eventBus: AppEventBusService,
    private readonly apiConfig: ApiConfig,
    @Inject(TeamApiService) private readonly Team: TeamApiService,
    @Inject(MarketplaceProductApiService) private readonly MarketplaceProduct: MarketplaceProductApiService
  ) {
    this.send = (config) => firstValueFrom(apiClient.request(config));
    this.initOAuthParams();
    this.initSupportEligibility();
    this.readNetwork();
    const networkIds = this.localUserProfile.get('networkTagIds');
    this.hasMultipleNetworks = Array.isArray(networkIds) && networkIds.length > 1;
    const twoFactorAuthType = this.localUserProfile.get('twoFactorAuthType');
    this.twoFactorAuthEnabled = twoFactorAuthType === 'APP' || twoFactorAuthType === 'EMAIL';
  }

  ngOnInit(): void {
    this.window.addEventListener('storage', this.onStorage);
    if (this.bookingRequested && this.twoFactorAuthEnabled && !this.networkMismatch()) {
      this.loadBookingChoices();
    }
    const needsNetworks = this.agentOAuth && this.twoFactorAuthEnabled && this.hasMultipleNetworks && (this.resourceIsBare() || this.networkMismatch());
    if (!needsNetworks) {
      return;
    }
    this.Network.listOptions(resourceOptionParams({ids: this.localUserProfile.get('networkTagIds'), minimal: true, useNetworkTags: true})).subscribe({
      next: (page) => {
        this.networks = [...(page?.results || [])].sort((a, b) => String(a.name).localeCompare(String(b.name)));
      },
      error: () => {
        this.networksLoadFailed = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.window.removeEventListener('storage', this.onStorage);
  }

  // A switch in another tab saves the new network before its name has loaded, so a later event can change only the name.
  private readonly onStorage = (): void => {
    this.syncNetwork();
  };

  /** Reads the session's network again. Returns true, and says so on the page, when another tab has switched it since the page last read it. */
  private syncNetwork(): boolean {
    const previous = this.currentNetworkId;
    this.readNetwork();
    this.networkChangedElsewhere = this.networkChangedElsewhere || this.currentNetworkId !== previous;
    return this.currentNetworkId !== previous;
  }

  private readNetwork(): void {
    this.currentNetworkId = this.localNetworkProfile.get('networkId') || null;
    this.currentNetworkTagId = this.localNetworkProfile.get('tagId') || null;
    this.networkName = this.localNetworkProfile.get('networkName') || this.currentNetworkId || 'Adnuntius';
    this.selectedNetworkId = this.currentNetworkId;
  }

  /** A connector for the bare endpoint gets a lease in whichever network the page consents for, so the user may choose it. */
  showsNetworkChoice(): boolean {
    return this.resourceIsBare() && !this.supportRequested && !!this.networks && this.networks.length > 1;
  }

  /** A per-network connector for a network other than the open one. The server refuses to mint its lease here, so the page offers a switch instead. */
  networkMismatch(): boolean {
    return !!this.resourceNetworkId && !!this.currentNetworkId && this.resourceNetworkId !== this.currentNetworkId;
  }

  /** How the page can resolve a mismatch: switch to the connector's network, tell the user they cannot use it, or (networks not known) tell them how. */
  mismatchResolution(): 'switch' | 'unavailable' | 'unknown' {
    if (this.resourceNetwork()) {
      return 'switch';
    }
    return !this.hasMultipleNetworks || this.networks ? 'unavailable' : 'unknown';
  }

  resourceNetwork(): any {
    return (this.networks || []).find((network: any) => network.id === this.resourceNetworkId) || null;
  }

  chooseNetwork(networkId: string): Promise<void> {
    const network = (this.networks || []).find((candidate: any) => candidate.id === networkId);
    this.syncNetwork();
    if (!network || networkId === this.currentNetworkId) {
      return Promise.resolve();
    }
    this.selectedNetworkId = networkId;
    return this.switchNetwork(network);
  }

  switchToResourceNetwork(): Promise<void> {
    this.syncNetwork();
    return this.networkMismatch() ? this.switchNetwork(this.resourceNetwork()) : Promise.resolve();
  }

  private switchNetwork(network: any): Promise<void> {
    if (!network || this.switchingTo || this.authorizing) {
      return Promise.resolve();
    }
    this.switchingTo = network;
    this.switchFailedName = null;
    return Promise.resolve(this.adnNetworkHelper.switchNetwork({}, this.eventBus, network)).catch(() => undefined).then(() => {
      // A switch that worked reloads this address in the new network. One that failed has put the previous network back, and the page must go on naming
      // the network it will consent for, not the one the user picked.
      if (this.localNetworkProfile.get('networkId') === network.id) {
        return;
      }
      this.switchingTo = null;
      this.switchFailedName = network.name || network.id;
      this.readNetwork();
    });
  }

  private resourceIsBare(): boolean {
    return !this.oauthResource || this.oauthResource === this.apiConfig.getUri('mcp');
  }

  /** The client asked for support access this user cannot be granted. Minting anything here gives it a token its endpoint refuses, so refuse instead. */
  supportBlocked(): boolean {
    return this.clientDeclaredSupport && !this.supportEligible;
  }

  /** Buyer teams and marketplace products the user can see. The server refuses a team the user may not book for. */
  private loadBookingChoices(): void {
    forkJoin([
      this.Team.listOptions({pageSize: 501, filterBy: 'type', filterByValue: 'AX_ADVERTISER'}),
      this.MarketplaceProduct.listOptions({pageSize: 501})
    ]).subscribe({
      next: ([teams, products]) => {
        this.bookingTeams = teams?.results || [];
        this.bookingProducts = products?.results || [];
      },
      error: () => {
        this.bookingLoadFailed = true;
      }
    });
  }

  isTeamChosen(teamId: string): boolean {
    return this.chosenTeamIds.indexOf(teamId) > -1;
  }

  isProductChosen(productId: string): boolean {
    return this.chosenProductIds.indexOf(productId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    this.chosenTeamIds = withChoice(this.chosenTeamIds, teamId, (event.target as HTMLInputElement).checked);
    // Drop chosen products that no chosen team can buy.
    const offered = this.productsOfChosenTeams().map((product) => product.id);
    this.chosenProductIds = this.chosenProductIds.filter((id) => offered.indexOf(id) > -1);
  }

  toggleProduct(productId: string, event: Event): void {
    this.chosenProductIds = withChoice(this.chosenProductIds, productId, (event.target as HTMLInputElement).checked);
  }

  productsOfChosenTeams(): any[] {
    return (this.bookingProducts || []).filter((product) => (product.teams || []).some((team: any) => this.isTeamChosen(team.id)));
  }

  cancel(): void {
    this.cancelled = true;
    if (!this.oauthRedirectUri) {
      return;
    }
    setTimeout(() => this.redirectDenied(), 2000);
  }

  private redirectDenied(): void {
    if (!this.oauthRedirectUri) {
      return;
    }
    const sep = this.oauthRedirectUri.indexOf('?') === -1 ? '?' : '&';
    this.window.location.href = this.oauthRedirectUri + sep
      + 'error=access_denied'
      + '&state=' + encodeURIComponent(this.oauthState);
  }

  authorizeOAuth(): Promise<void> {
    if (this.supportBlocked() || this.authorizing || !this.agentOAuth || !this.twoFactorAuthEnabled || this.switchingTo
      || (this.bookingRequested && !this.chosenTeamIds.length)) {
      return Promise.resolve();
    }
    // The user consents to the network the page names. When another tab has switched away from it, show the new network and let them consent again.
    if (this.syncNetwork() || this.networkMismatch()) {
      return Promise.resolve();
    }
    this.oauthError = null;
    this.oauthErrorMessage = null;
    this.authorizing = true;
    const endpoint = this.apiConfig.getVersionedUri('apikeys/authorize-code');
    const payload = {
      name: this.agentApp + ' on ' + (this.window.navigator.platform || 'this device'),
      scopes: this.bookingRequested ? BOOKING_SCOPES : QUERY_SCOPES,
      expiry: new Date(Date.now() + AGENT_LEASE_HOURS * 3600 * 1000),
      readOnly: !this.bookingRequested
    };
    const params: Record<string, string | string[]> = {
      code_challenge: this.oauthCodeChallenge || '',
      code_challenge_method: this.oauthCodeChallengeMethod,
      redirect_uri: this.oauthRedirectUri || '',
      state: this.oauthState,
      client_id: this.oauthClientId || ''
    };
    if (this.bookingRequested) {
      params.team = this.chosenTeamIds;
      // Without a product param the agent can book any product of the chosen teams.
      if (this.chosenProductIds.length) {
        params.product = this.chosenProductIds;
      }
    }
    if (this.clientDeclaredSupport || (!this.oauthResource && this.supportEligible && this.supportRequested)) {
      // The support audience: the server mints a support lease only when `resource` equals its own <issuer>/api/mcp/support string. Only ever chosen here when
      // the client declared no resource of its own — overriding a declared one would widen the lease past the request and leave the client a token its own
      // endpoint refuses.
      params.resource = this.apiConfig.getUri('mcp/support');
    } else if (this.oauthResource) {
      // Echo back what the client asked for. Dropping it bound the lease to the bare endpoint, so a per-network connector authenticated and then failed the
      // audience check on every call. The server refuses a resource that is not one of its own, so passing it through cannot widen the lease.
      params.resource = this.oauthResource;
    }
    const network = this.currentNetworkTagId || this.currentNetworkId;
    if (network) {
      // Pin the lease to the network the page names. Otherwise the request takes whichever network the shared session has open when it is sent.
      params.context = network;
    }
    return this.send({
      method: 'POST',
      url: endpoint,
      params,
      preserveContext: !!network,
      body: payload
    }).then((data: any) => {
      data = data || {};
      const redirectUri = this.oauthRedirectUri || '';
      const sep = redirectUri.indexOf('?') === -1 ? '?' : '&';
      // iss is RFC 9207 issuer identification — the client checks it against the authorization server it began the flow with. The server advertises
      // authorization_response_iss_parameter_supported, so a strict client rejects an authorization response without it; only omit when the server didn't send one.
      const iss = data.iss ? '&iss=' + encodeURIComponent(data.iss) : '';
      this.window.location.href = redirectUri + sep
        + 'code=' + encodeURIComponent(data.code || '')
        + '&state=' + encodeURIComponent(data.state || this.oauthState)
        + iss;
    }, (response: any) => {
      const data = (response && response.data) || null;
      this.authorizing = false;
      this.oauthError = data || {};
      this.oauthErrorMessage = oauthErrorMessage(data);
    });
  }

  // Mirrors the server's mint gate (ApiKeyResource): system-network role (canMasquerade), authenticator-app 2FA, not the super/internal
  // accounts, not masquerading. Display-only — the server refuses ineligible mints regardless. Masquerade is checked via both signals:
  // passkey sign-in leaves Session.masqueraderUsername empty while the profile still carries supportUserId.
  private initSupportEligibility(): void {
    const masquerading = !!Session.obtainData().masqueraderUsername || !!this.localUserProfile.get('supportUserId');
    this.supportEligible = this.agentOAuth
      && !!this.localUserProfile.get('canMasquerade')
      && this.localUserProfile.get('twoFactorAuthType') === 'APP'
      && !this.localUserProfile.isSpecialUser()
      && !masquerading;
    this.clientDeclaredSupport = this.agentOAuth && this.oauthResource === this.apiConfig.getUri('mcp/support');
    // Only once it can actually be granted: a blocked request that still reads as requested shows the consent prose next to the refusal.
    if (this.clientDeclaredSupport && this.supportEligible) {
      this.supportRequested = true;
    }
  }

  private initOAuthParams(): void {
    const routeParams = this.uiRouter.globals.params;
    this.oauthCodeChallenge = routeParams.code_challenge || null;
    this.oauthCodeChallengeMethod = routeParams.code_challenge_method || 'S256';
    this.oauthRedirectUri = VALID_OAUTH_REDIRECT.test(routeParams.redirect_uri || '')
      ? routeParams.redirect_uri
      : null;
    this.oauthState = routeParams.state || '';
    this.oauthClientId = routeParams.client_id || null;
    this.oauthResource = routeParams.resource || null;
    const resource = parseMcpResource(this.oauthResource, this.apiConfig.getUri('mcp'));
    this.resourceNetworkId = resource.networkId;
    this.agentOAuth = !!(this.oauthCodeChallenge && this.oauthRedirectUri && this.oauthClientId);
    this.bookingRequested = this.agentOAuth && resource.booking;
    this.agentApp = this.agentOAuth ? (routeParams.app || this.oauthClientId) : null;

    if (this.agentOAuth && this.oauthRedirectUri) {
      try {
        const parsed = new URL(this.oauthRedirectUri);
        this.oauthRedirectHost = parsed.host;
        this.oauthRedirectIsLoopback = (parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost');
      } catch (_error) {
        this.oauthRedirectHost = this.oauthRedirectUri;
        this.oauthRedirectIsLoopback = false;
      }
    }
  }
}

/**
 * The network a per-network resource names (one path segment after the MCP endpoint, as ApiKeyResource.otherNetworkIn reads it), and whether /booking
 * follows it.
 */
function parseMcpResource(resource: string | null, mcpUri: string): {networkId: string | null, booking: boolean} {
  const prefix = mcpUri + '/';
  if (!resource || resource.indexOf(prefix) !== 0) {
    return {networkId: null, booking: false};
  }
  const path = resource.substring(prefix.length);
  const booking = path.endsWith(BOOKING_SUFFIX);
  const segment = booking ? path.substring(0, path.length - BOOKING_SUFFIX.length) : path;
  const networkId = !segment || /[/?#]/.test(segment) || segment === 'support' || segment === 'source' ? null : segment;
  return {networkId, booking: booking && !!networkId};
}

function withChoice(ids: string[], id: string, chosen: boolean): string[] {
  return ids.filter((other) => other !== id).concat(chosen ? [id] : []);
}
